import React from 'react';
import { StaticImage, GatsbyImage, getImage } from 'gatsby-plugin-image';
import SectionLayout from '../layouts/SectionLayout/index';
import RawHTML from '@components/Common/RawHTML/index';
import useDataScienceProgram from '@src/hooks/useDataScienceProgram';

const CrioComparisionSection = React.memo(function CrioComparisionSection({
  data,
  desktopImage,
  mobileImage,
  isTypeQA,
  isAiSkillDevelopmentPage,
  ...props
}) {
  const { isDataSciencePage } = useDataScienceProgram();

  // yml-driven images (GatsbyImage) take precedence. When absent we fall back
  // to the `desktopImage`/`mobileImage` prop nodes, and finally to the
  // hardcoded StaticImage variants below.
  const ymlDesktopImage = getImage(data?.desktopImage?.childImageSharp);
  const ymlMobileImage = getImage(data?.mobileImage?.childImageSharp);

  return (
    <SectionLayout
      heading={
        <div className="text-xl sm:text-4xl">
          {data?.heading ? (
            <RawHTML>{data.heading}</RawHTML>
          ) : (
            <>Real work experience, Real project-based learning with Crio </>
          )}
        </div>
      }
      description={
        // A plain yml string is rendered by SectionLayout's own RawHTML wrapper.
        data?.description ? (
          data.description
        ) : isDataSciencePage ? (
          <>
            The best way to learn a skill is by applying what you learn in real
            scenarios. At Crio, we take it a notch higher by giving you actual
            work experience-based learning. So, instead of just watching
            tutorials, you actually experience analysing and solving complex
            problems like professional analysts. <br></br>Basically, how a real
            professional working in a leading tech company would grow their
            career.
          </>
        ) : isAiSkillDevelopmentPage ? (
          <>
            We don’t just teach — we go a notch higher and immerse you in
            real-world learning.
            <br />
            <span className="font-bold">
              Empowering you to build your Skill Quotient to master real skills
              and deliver impactful results.
            </span>
            <br />
            In today’s competitive, AI-driven world, we ensure you gain the
            outcomes that get you hired.
          </>
        ) : (
          <>
            The best way to learn a skill is by applying what you learn in real
            scenarios. At Crio, we take it a notch higher by giving you actual
            work experience-based learning. So, instead of just watching
            tutorials, you actually experience building real products like
            professional engineers.<br></br>
            <br></br> Basically, how a real professional working in a leading
            product company would grow their career.<br></br>
          </>
        )
      }
      {...props}
    >
      <div className="container">
        <div className="mb-10 flex justify-center overflow-hidden rounded-[20px]">
          <div className="hidden max-w-[900px] sm:block">
            {ymlDesktopImage ? (
              <GatsbyImage
                image={ymlDesktopImage}
                alt={
                  data?.desktopImageAlt ||
                  "A table displaying different course USPs, illustrating how Crio.Do's program excels compared to competitors."
                }
                imgStyle={{ objectFit: 'contain' }}
              />
            ) : desktopImage ? (
              desktopImage
            ) : isAiSkillDevelopmentPage ? (
              <StaticImage
                width={1920}
                loading="lazy"
                src="../../../images/v5/CrioComparision/AI-Skill-Development-Crio-Comparison-Desktop.webp"
                placeholder="blurred"
                alt="A table displaying different course USPs, illustrating how Crio.Do's AI Skill Development program excels compared to competitors."
              />
            ) : isDataSciencePage ? (
              <StaticImage
                width={1920}
                loading="lazy"
                src="../../../images/v5/CrioComparision/Table-Desktop-DS.webp"
                placeholder="blurred"
                alt="A table displaying different course USPs, illustrating how Crio.Do's Data Analytics & Data Science Program excels compared to competitors."
              />
            ) : isTypeQA ? (
              <StaticImage
                width={1920}
                loading="lazy"
                src="../../../images/v5/CrioComparision/Table-Desktop-QA.webp"
                placeholder="blurred"
                alt="A table displaying different course USPs, illustrating how Crio.Do's QA program excels compared to competitors."
              />
            ) : (
              <StaticImage
                width={1920}
                loading="lazy"
                src="../../../images/v5/CrioComparision/Table-Desktop.webp"
                placeholder="blurred"
                alt="A table displaying different course USPs, illustrating how Crio.Do's Fellowship program excels compared to competitors."
              />
            )}
          </div>
          <div className="max-w-full sm:hidden ">
            {ymlMobileImage ? (
              <GatsbyImage
                image={ymlMobileImage}
                alt={
                  data?.mobileImageAlt ||
                  data?.desktopImageAlt ||
                  "A table displaying different course USPs, illustrating how Crio.Do's program excels compared to competitors."
                }
                imgStyle={{ objectFit: 'contain' }}
              />
            ) : mobileImage ? (
              mobileImage
            ) : isAiSkillDevelopmentPage ? (
              <StaticImage
                width={1000}
                loading="lazy"
                src="../../../images/v5/CrioComparision/AI-Skill-Development-Crio-Comparison-Mobile.webp"
                placeholder="blurred"
                alt="A table displaying different course USPs, illustrating how Crio.Do's AI Skill Development program excels compared to competitors."
              />
            ) : isDataSciencePage ? (
              <StaticImage
                width={1000}
                loading="lazy"
                src="../../../images/v5/CrioComparision/Table-Mobile-DS.webp"
                alt="A table displaying different course USPs, illustrating how Crio.Do's Data Analytics & Data Science Program excels compared to competitors."
              />
            ) : isTypeQA ? (
              <StaticImage
                width={1000}
                loading="lazy"
                src="../../../images/v5/CrioComparision/Table-Mobile-QA.webp"
                placeholder="blurred"
                alt="A table displaying different course USPs, illustrating how Crio.Do's QA program excels compared to competitors."
              />
            ) : (
              <StaticImage
                width={1000}
                loading="lazy"
                src="../../../images/v5/CrioComparision/Table-Mobile.webp"
                placeholder="blurred"
                alt="A table displaying different course USPs, illustrating how Crio.Do's Fellowship program excels compared to competitors."
              />
            )}
          </div>
        </div>
        {(data?.footerText || !isAiSkillDevelopmentPage) && (
          <p
            className={`${
              props.dark ? 'text-v5-neutral-300' : 'text-v5-neutral-400'
            } text-center leading-loose sm:px-20`}
          >
            {data?.footerText ? (
              <RawHTML>{data.footerText}</RawHTML>
            ) : (
              <>
                {' '}
                Recruiters too, look for real project experience when hiring
                tech professionals and here at Crio, we strive to provide just
                that. We empower learners with high-quality applied learning
                opportunities and build skills that translate into career growth
                and success.
              </>
            )}
          </p>
        )}
      </div>
    </SectionLayout>
  );
});

export default CrioComparisionSection;
